import React from 'react';
import { motion } from 'framer-motion';
import { FiExternalLink, FiCalendar, FiAward, FiCheck, FiClock, FiTrendingUp, FiImage } from 'react-icons/fi';

const Certifications = () => {
  const certifications = [
    {
      id: 1,
      title: "AWS Academy Cloud Foundations",
      issuer: "Amazon Web Services",
      date: "Aug 2024",
      status: "Completed",
      skills: ["AWS Cloud", "AWS Support", "AWS Architecture"],
      verifyUrl: "https://www.credly.com/badges/7c8e5ffb-5ef3-48cc-b0ba-79ce1b6a0de6/linked_in_profile",
      verifyType: "url",
      imageUrl: "/images/certifications/ibm-logo.png", // Fixed to use correct AWS logo
      level: "Intermediate"
    },
    {
      id: 2,
      title: "Fundamentals of Cybersecurity",
      issuer: "Zscaler",
      date: "Mar 2025",
      status: "Completed",
      skills: ["Cybersecurity", "Cloud", "Training"],
      verifyUrl: "/images/certifications/Zscaler-certificate.png",
      verifyType: "image",
      imageUrl: "/images/certifications/Zscaler-logo.png",
      level: "Advanced"
    },
    {
      id: 3,
      title: "RedHat System Administration II",
      issuer: "RedHat",
      date: "Mar 2025",
      status: "Completed",
      skills: ["RedHat", "Linux", "RHCSA"],
      verifyUrl: "https://www.credly.com/badges/aeb61261-bb43-42f6-8984-c8c396cd5e4d/linked_in_profile",
      verifyType: "url",
      imageUrl: "/images/certifications/rh-logo.png",
      level: "Intermediate"
    },
    {
      id: 4,
      title: "Database and SQL for Data Science with Python",
      issuer: "IBM",
      date: "Aug 2025",
      status: "Completed",
      skills: ["SQL", "Database", "Python", "Data Analysis"],
      verifyUrl: "https://coursera.org/verify/B1II3YT5KOXX",
      verifyType: "url",
      imageUrl: "/images/certifications/aws-logo.png", // Fixed to use correct IBM logo
      level: "Intermediate"
    }
  ];

  // Function to handle certificate verification
  const handleVerifyClick = (cert) => {
    if (cert.verifyType === "url") {
      window.open(cert.verifyUrl, '_blank', 'noopener,noreferrer');
    } else if (cert.verifyType === "image") {
      window.open(cert.verifyUrl, '_blank');
    }
  };

  // Optimized animation variants for smoother performance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { 
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <section 
      id="certifications" 
      className="py-8 md:py-12 bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-gray-900 dark:to-indigo-950 transition-colors duration-300 relative overflow-hidden"
      aria-label="Professional Certifications Section"
    >
      {/* Simplified background pattern for better performance */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-primary/5 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Enhanced Header */}
        <motion.div
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="text-center mb-8 md:mb-12"
        >
          <motion.div 
            className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-2xl mb-4 shadow-lg"
            whileHover={{ 
              scale: 1.03,
              transition: { duration: 0.2 }
            }}
          >
            <FiAward className="w-8 h-8 text-white" />
          </motion.div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Certifications
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Industry-recognized credentials showcasing my technical expertise and commitment to continuous learning
          </p>
          
          {/* Progress indicator */}
          <div className="mt-6 inline-flex items-center space-x-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-md border border-white/20 dark:border-gray-700/50">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full" aria-hidden="true"></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {certifications.filter(cert => cert.status === 'Completed').length} Completed
              </span>
            </div>
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" aria-hidden="true"></div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-500 rounded-full" aria-hidden="true"></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {certifications.filter(cert => cert.status === 'In Progress').length} In Progress
              </span>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Cards Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-8"
          role="list"
          aria-label="Certification cards"
        >
          {certifications.map((cert, index) => (
            <motion.article
              key={cert.id}
              variants={cardVariants}
              className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700 flex flex-col h-full"
              whileHover={{ 
                y: -4, 
                scale: 1.01,
                transition: { duration: 0.2 }
              }}
              role="listitem"
            >
              {/* Header */}
              <div className="relative h-28 bg-gradient-to-br from-gray-700 to-gray-800 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                {/* Decorative pattern */}
                <div 
                  className="absolute inset-0 bg-white/10 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.3),transparent_50%)]"
                  aria-hidden="true"
                ></div>
                
                {/* Logo Container */}
                <motion.div 
                  className="relative w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-xl z-10"
                  whileHover={{ 
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                >
                  <img 
                    src={cert.imageUrl} 
                    alt={`${cert.issuer} certification logo`}
                    className="w-10 h-10 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-10 h-10 bg-blue-100 rounded items-center justify-center text-blue-600 hidden">
                    <FiAward className="w-6 h-6" aria-label="Default certification icon" />
                  </div>
                </motion.div>
                
                {/* Status Badge */}
                <motion.div 
                  className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold flex items-center space-x-1 ${
                    cert.status === 'Completed' 
                      ? 'bg-green-500/90 text-white backdrop-blur-sm'
                      : 'bg-orange-500/90 text-white backdrop-blur-sm'
                  }`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 + (index * 0.05), duration: 0.3 }}
                  aria-label={`Status: ${cert.status}`}
                >
                  {cert.status === 'Completed' ? (
                    <FiCheck className="w-3 h-3" aria-hidden="true" />
                  ) : (
                    <FiClock className="w-3 h-3" aria-hidden="true" />
                  )}
                  <span className="hidden sm:inline">{cert.status}</span>
                </motion.div>

                {/* Level Badge */}
                <div className="absolute bottom-3 left-3 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                  <span className="text-xs font-medium text-white">{cert.level}</span>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-5 flex flex-col flex-grow">
                {/* Title and Issuer */}
                <div className="mb-4">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white mb-1 line-clamp-2 group-hover:text-primary transition-colors duration-200">
                    {cert.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    {cert.issuer}
                  </p>
                </div>
                
                {/* Date Info */}
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                  <FiCalendar className="w-4 h-4 mr-2 text-primary" aria-hidden="true" />
                  <span>{cert.date}</span>
                </div>

                {/* Skills */}
                <div className="mb-4 flex-grow">
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-2">
                    <FiTrendingUp className="w-3 h-3 mr-1" aria-hidden="true" />
                    Skills Covered
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {cert.skills.slice(0, 2).map((skill, skillIndex) => (
                      <span 
                        key={`${cert.id}-${skill}-${skillIndex}`}
                        className="px-2 py-1 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium transition-transform duration-200 hover:scale-105"
                      >
                        {skill}
                      </span>
                    ))}
                    {cert.skills.length > 2 && (
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium">
                        +{cert.skills.length - 2}
                      </span>
                    )}
                  </div>
                </div>

                {/* Verify Button */}
                <div className="mt-auto">
                  {cert.verifyUrl ? (
                    <motion.button
                      onClick={() => handleVerifyClick(cert)}
                      className="w-full inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-primary to-secondary text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 cursor-pointer"
                      whileHover={{ 
                        scale: 1.01,
                        y: -1,
                        transition: { duration: 0.15 }
                      }}
                      whileTap={{ scale: 0.98 }}
                      aria-label={`${cert.verifyType === 'image' ? 'View' : 'Verify'} ${cert.title} certificate`}
                    >
                      {cert.verifyType === 'image' ? (
                        <FiImage className="w-4 h-4" aria-hidden="true" />
                      ) : (
                        <FiExternalLink className="w-4 h-4" aria-hidden="true" />
                      )}
                      <span>
                        {cert.verifyType === 'image' ? 'View Certificate' : 'Verify Certificate'}
                      </span>
                    </motion.button>
                  ) : (
                    <div 
                      className="w-full inline-flex items-center justify-center space-x-2 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-not-allowed"
                      aria-label="Certificate verification pending"
                    >
                      <FiClock className="w-4 h-4" aria-hidden="true" />
                      <span>Completion Pending</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Glow effect */}
              <div 
                className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                aria-hidden="true"
              ></div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Certifications;
